import type { TeamNode } from "@/lib/admin/team-tree";
import { buildTeamTree } from "@/lib/admin/team-tree";

/**
 * Who a Copilot key is for. GitHub attributes nothing per developer on the
 * custom-models path (one key = one entry in the picker), so the only way an
 * organization gets department-level spend from that path is one key per
 * department; this is the shape the Connect Copilot dialog offers next to the
 * organization-wide key it always minted.
 */
export type CopilotKeyScope = "organization" | "team";

export const DEFAULT_COPILOT_KEY_NAME = "GitHub Copilot";

/** The name we propose for the AI Reserve key; "GitHub Copilot · <department>" for a team key. */
export function copilotKeyNameFor(scope: CopilotKeyScope, teamName: string | null | undefined): string {
  const trimmed = teamName?.trim();
  return scope === "team" && trimmed ? `${DEFAULT_COPILOT_KEY_NAME} · ${trimmed}` : DEFAULT_COPILOT_KEY_NAME;
}

/**
 * True when the current name is one this module produced, so a new team pick
 * may replace it; a name the admin typed is left alone.
 */
export function isSuggestedCopilotKeyName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed === DEFAULT_COPILOT_KEY_NAME || trimmed.startsWith(`${DEFAULT_COPILOT_KEY_NAME} · `);
}

/**
 * The label the GitHub admin gives the API-key entry on GitHub's side. It
 * prefixes every model in the developer's picker, so the department name is
 * what tells a developer which entry is theirs.
 */
export function githubEntryNameFor(scope: CopilotKeyScope, teamName: string | null | undefined): string {
  const trimmed = teamName?.trim();
  return scope === "team" && trimmed ? `AI Reserve · ${trimmed}` : "AI Reserve";
}

export interface CopilotTeamOption {
  id: string;
  /** Team name, indented by depth so sub-teams read under their parent in a flat Select. */
  label: string;
  name: string;
  depth: number;
}

/**
 * Teams in hierarchy order (parents before children, siblings as given), each
 * labelled with its depth. Name-sorted input keeps siblings alphabetical.
 */
export function copilotTeamOptions<T extends TeamNode & { name: string }>(teams: readonly T[]): CopilotTeamOption[] {
  const byId = new Map(teams.map((team) => [team.id, team]));
  const tree = buildTeamTree(teams.map(({ id, parentTeamId }) => ({ id, parentTeamId })));
  return tree.dfsOrder.map((id) => {
    const team = byId.get(id)!;
    const depth = tree.depthOf.get(id) ?? 0;
    return { id, name: team.name, depth, label: `${"\u2003".repeat(depth)}${team.name}` };
  });
}
